<template lang="pug" src="./business.pug"></template>
<style lang="scss" src="./business.scss" scoped></style>

<script>
import router from '@/router'
import validate from '@/mixins/validate'
import scroll from '@/utils/scroll'
import request from '@/utils/request'

import { mapState, mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { numeric } from 'vuelidate/lib/validators'
import lockDialog from '@/components/LockDialog'

export default {
    components: {
        'v-lock-dialog': lockDialog
    },
    mixins: [validationMixin, validate],
    data: () => ({
        isConfirmed: true,
        filters: {
            businessId: null,
            fromDate: null,
            toDate: null,
            leader: null,
            businessEmail: null,
            query: null
        },
        refillDialog: false,
        refillSum: null,
        idToRefill: null,
        businessBalance: null,
        refillPromoBalance: null,
        activeAccountId: null,
        lockDialog: false,
        feeTypes: ['fee', 'telephonyFee'],
        newFee: null,
        sorting: {},
        loading: false,
        changeLimits: {
            data: {
                clientLimit: null
            },
            referrer: {},
            dialog: false,
            resetFeeDate: null
        },
        newLimitedTo: null,
        newAmount: null
    }),
    validations: {
        filters: {
            businessId: {
                numeric
            }
        },
        newFee: {
            numeric: function(value) {
                if (!value) {
                    return true
                }

                return /^\d+$/.test(value) && value >= 0 && value <= 100
            }
        },
        changeLimits: {
            data: {
                clientLimit: {
                    max: function(value) {
                        const referrer = this.changeLimits.referrer

                        if (Object.keys(referrer).length > 0) {
                            const { unlimitedRights, clientBase } = referrer

                            if (unlimitedRights) {
                                return value <= 10000
                            }

                            return value <= (clientBase - this.usedClients)
                        }

                        return true
                    }
                }
            }
        }
    },
    computed: {
        ...mapGetters(['type', 'permissions', 'user']),
        ...mapState({
            businesses: state => state.business.items,
            businessesEmails: state => state.data.businesses,
            isTableLoad: state => state.business.isLoaded,
            appliedFilters: state => state.business.filters,
            leaders: state => state.data.leaders,
            pagination: state => state.business.pagination,
            numberOfBusinesses: (state) => state.data.numberOfBusinesses,
            numberOfUnconfirmedBusinesses: (state) => state.data.numberOfUnconfirmedBusinesses,
            user: state => state.app.user
        }),
        refillDisabled: function() {
            const lessThenBalance = parseInt(this.businessBalance) + parseInt(this.refillSum) < 0
            return !this.refillSum || !this.refillSum.match(/^-?\d+$/) || this.refillPromoBalance ? false : lessThenBalance
        },
        canWatchLeaders: function() {
            return this.permissions && this.permissions.includes('watch-leaders')
        },
        confirmedHeaders: function() {
            const headers = [
                { text: '№', sortable: false, show: ['leader'], number: true },
                { text: 'businessId', value: 'id' },
                { text: 'email', value: 'email' },
                { text: 'firstName', value: '"Account"."firstName"' },
                { text: 'lastName', value: '"Account"."lastName"' },
                { text: 'referrerEmail', value: '"referrer"."email"', show: ['admin'] },
                { text: 'phoneNumber', value: '"Account"."phoneNumber"' },
                { text: 'balance', value: 'balance' },
                { text: 'fee', value: 'fee', show: ['admin'] },
                { text: 'telephonyFee', show: ['admin'], sortable: false },
                { text: 'leaderFee', show: ['admin', 'leader', 'business'], sortable: false },
                { text: 'leaderTelephonyFee', show: ['admin', 'leader', 'business'], sortable: false },
                { text: 'clients', sortable: false },
                { text: 'clientLimit', value: 'clientLimit' },
                { text: 'limitedTo', value: '"limitedTo"' },
                { text: 'processedAfterRestrictions', value: '"processedAfterRestrictions"' },
                { text: 'purchased', sortable: false, show: ['admin'] },
                { text: 'autoPromoBalance', value: '"autoPromoBalance"' },
                { text: 'amountToPay', value: '"amountToPay"' },
                { text: 'amountPaid', value: '"amountPaid"' },
                { text: 'createdAt', value: '"Account"."createdAt"' },
                { text: 'actions', sortable: false }
            ]

            if (!this.canWatchLeaders) {
                const removeIndex = headers.findIndex(it => it.text === 'referrerEmail')

                if (removeIndex !== -1) {
                    headers.splice(removeIndex, 1)
                }
            }

            const leaderFeeHeaders = [{
                text: 'leaderFee',
                enabled: this.user && this.user.processedAfterRestrictions
            }, {
                text: 'leaderTelephonyFee',
                enabled: this.canShowLeaderFee
            }]

            leaderFeeHeaders.forEach(header => {
                if (this.type != 'admin' && !header.enabled) {
                    const headerIndex = headers.findIndex(it => it.text === header.text)

                    if (headerIndex !== -1) {
                        headers.splice(headerIndex, 1)
                    }
                }
            })

            return headers
                .filter(it => it.show ? this.isShow(it.show) : true)
                .filter(it => typeof it.hidden == 'boolean' ? !it.hidden : true)
                .map(it => Object.assign(it, {
                    text: it.number ? it.text : this.$vuetify.lang.t(`$vuetify.pages.Businesses.table.${it.text}`)
                }))
        },
        unconfirmedHeaders: function() {
            const headers = [
                { text: '№', sortable: false, show: ['leader'], number: true },
                { text: 'businessId', value: 'id' },
                { text: 'email', value: 'email' },
                { text: 'firstName', value: '"Account"."firstName"' },
                { text: 'lastName', value: '"Account"."lastName"' },
                { text: 'createdAt', value: '"Account"."createdAt"' }
            ]

            if (this.canWatchLeaders) {
                const insertIndex = headers.findIndex(it => it.text === 'lastName')
                headers.splice(insertIndex + 1, 0, { text: 'referrerEmail', value: '"referrer"."email"', show: ['admin'] })
            }

            return headers
                .filter(it => it.show ? this.isShow(it.show) : true)
                .map(it => Object.assign(it, {
                    text: it.number ? it.text : this.$vuetify.lang.t(`$vuetify.pages.Businesses.table.${it.text}`)
                }))
        },
        canLockUserAccount: function() {
            return this.permissions && this.permissions.includes('lock-account')
        },
        usedClients() {
            if (!this.changeLimits.referrer) return
            return this.changeLimits.referrer.distributedClients - (this.changeLimits.oldClientLimit || 0)
        },
        leaderFeeTypes() {
            return [{
                value: 'leaderFee',
                enabled: this.type == 'admin' || this.user.processedAfterRestrictions
            }, {
                value: 'leaderTelephonyFee',
                enabled: this.type == 'admin' || this.canShowLeaderFee
            }]
        },
        canShowLeaderFee() {
            return this.user && this.user.includeTelephony && this.user.processedAfterRestrictions
        },
        tableTitle() {
            const translationPath = '$vuetify.pages.Businesses'

            const t = (key, value) => this.$vuetify.lang.t(`${translationPath}.${key}`, value)

            const confirmed = this.type == 'admin' ? 'adminTitle' : 'tableTitleConfirmed'
            const unconfirmed = this.type == 'admin' ? 'adminUnconfirmedTitle' : 'tableTitleUnconfirmed'

            return {
                confirmed: t(confirmed, this.numberOfBusinesses),
                unconfirmed: t(unconfirmed, this.numberOfUnconfirmedBusinesses)
            }
        }
    },
    watch: {
        sorting: {
            handler() {
                this.getBusinesses()
            },
            deep: true
        }
    },
    created: function() {
        if (this.type === 'admin') {
            this.$store.dispatch('getLeaders')
        }

        if (this.type === 'leader') {
            this.$store.dispatch('getNumberOfReferrals')
        }

        this.filters = Object.assign({}, this.appliedFilters)
        this.getBusinesses()
    },
    beforeRouteLeave: function(to, from, next) {
        this.$store.commit('business/SET_PAGINATION', { page: 1, length: null })
        next()
    },
    methods: {
        getBusinessList(leaderChanged = false) {
            this.$store.dispatch('getBusinesses', Object.assign({}, this.filters.leader && {
                leaderId: this.filters.leader.id
            }))
            if (leaderChanged) {
                this.filters.businessEmail = null
            }
        },
        submit() {
            this.$store.commit('business/SET_FILTERS', Object.assign({}, this.filters))
            this.getBusinesses()
        },
        getBusinesses(page = 1, isScrollEnable = false, excel = false) {
            if (this.$v.$invalid) {
                this.$v.$touch()
            } else {
                this.$store.dispatch('business/getBusiness', {
                    excel,
                    page,
                    column: this.sorting.sortBy,
                    direction: this.sorting.descending ? 'desc' : 'asc',
                    ...this.appliedFilters,
                    isConfirmed: this.isConfirmed
                }).then(_ => {
                    if (isScrollEnable) {
                        scroll(this.$refs.table)
                    }

                    this.getBusinessList()
                })
            }
        },
        changeToDate(date) {
            this.filters.toDate = date
        },
        changeFromDate(date) {
            this.filters.fromDate = date
        },
        isShow(types) {
            return types.includes(this.type)
        },
        onPageChange(page) {
            if (page !== this.pagination.page) {
                this.getBusinesses(page, true)
            }
        },
        addAccount() {
            this.$store.commit('createAccount/SET_ACTIVE_TYPE', 'business')
            router.push('create-account')
        },
        unlockAccount(id) {
            if (this.loading) return
            this.loading = true

            this.$store.dispatch('changeActivity', {
                id, accountType: 'business'
            }).then(() => {
                this.loading = false
            })
        },
        onPurchasedChange(id) {
            this.$store.dispatch('changePurchased', id).then(status => {
                const account = this.businesses.findIndex(it => it.businessId == id)
                const value = status === 204 ? !account.purchased : false

                if (account) {
                    this.$set(account, 'purchased', value)
                }
            })
        },
        changeConfirmed() {
            this.isConfirmed = !this.isConfirmed
            if (this.isConfirmed) {
                this.$store.dispatch('getBusinesses')
            } else {
                this.$store.dispatch('getBusinesses', {
                    isConfirmed: false
                })
            }
            this.getBusinesses()
        },
        reset() {
            this.filters = Object.assign({}, {
                businessId: null,
                fromDate: null,
                toDate: null,
                leader: null,
                business: null,
                query: null
            })
            this.$store.commit('business/RESET_BUSINESS')
            this.getBusinesses()
        },
        openDialog(dialogType, id, balance, refillPromoBalance = false) {
            this[dialogType] = true
            this.activeAccountId = id
            this.businessBalance = balance
            this.refillPromoBalance = refillPromoBalance
        },
        async refillBalance() {
            const res = await this.$store.dispatch('refillBalance', {
                accountId: this.activeAccountId,
                sum: this.refillSum * 100
            })

            if (res.status == 200) {
                const index = this.businesses.findIndex(it => it.businessId == this.activeAccountId)

                if (index != -1) {
                    const business = this.businesses[index]
                    business.balance = parseInt(business.balance) + parseInt(this.refillSum * 100)
                }
            }

            this.resetData()
            this.refillDialog = false
        },
        resetData() {
            this.activeAccountId = null

            if (this.refillDialog) {
                this.refillSum = null
                this.refillPromoBalance = null
            }
        },
        closeDialog(dialogType) {
            this.resetData()
            this[dialogType] = false
        },
        onEditDialogOpen(item, type = 'fee') {
            this.newFee = item[type] || 0
        },
        async saveFee(accountId, index, type = 'fee') {
            if (this.$v.newFee.$invalid) {
                this.$v.newFee.$touch()
                return
            }

            await this.$store.dispatch('editUserFee', {
                accountId, type,
                newFee: this.newFee
            }).then(status => {
                if (status && status === 204) {
                    this.$set(this.businesses[index], type, this.newFee || 0)

                    this.$nextTick(() => {
                        this.newFee = null
                    })
                }
            })
        },
        exportToExcel() {
            this.getBusinesses(1, false, true)
        },
        changeLimitsHandler({ clientLimit, unlimitedRights, businessId, referrer, resetFeeDate }) {
            this.changeLimits = {
                data: {
                    clientLimit,
                    unlimitedRights,
                    accountId: businessId
                },
                oldClientLimit: clientLimit,
                referrer,
                dialog: true,
                resetFeeDate: !!resetFeeDate
            }
        },
        unlimitedRightsHandler(value) {
            if (value) {
                this.changeLimits.data.clientLimit = null
            }
        },
        resetChangeLimits() {
            this.changeLimits = {
                data: {
                    clientLimit: null
                },
                referrer: {},
                dialog: false,
                resetFeeDate: null
            }
        },
        async saveLimits() {
            if (this.$v.changeLimits.$invalid) {
                this.$v.changeLimits.$touch()
                return
            }

            if (this.loading) return

            this.loading = true

            if (!this.changeLimits.resetFeeDate) {
                request({
                    method: 'post',
                    url: `/account/${this.changeLimits.data.accountId}/resets/fee/exclude`
                })
                    .then(() => null)
                    .catch(err => {
                        console.error(err)
                    })
            }

            await request({
                method: 'post',
                url: 'limits',
                data: {
                    clientLimit: this.changeLimits.data.clientLimit || null,
                    ...Object.select(this.changeLimits.data, ['accountId', 'unlimitedRights'])
                }
            }).then(res => {
                this.getBusinesses()
                this.changeLimits.dialog = false
            }).catch(err => {
                if (err.response && err.response.data && err.response.data.message) {
                    this.$store.dispatch('addMessage', {
                        type: 'error',
                        message: err.response.data.message
                    }, { root: true })
                }
            }).finally(() => {
                this.loading = false
            })
        },
        showEditLimitedTo({ limitedTo, processedAfterRestrictions }) {
            return this.user.type == 'admin' && limitedTo && !processedAfterRestrictions
        },
        async saveLimitedTo(accountId, index) {
            if (this.loading || !this.newLimitedTo) return

            this.loading = true

            await request({
                method: 'put',
                url: '/limit/date',
                data: { accountId, limitedTo: this.newLimitedTo }
            }).then(res => {
                if (res.data) {
                    this.businesses[index].limitedTo = this.newLimitedTo
                }
            }).catch(err => {
                if (err.response && err.response.data && err.response.data.message) {
                    this.$store.dispatch('addMessage', {
                        type: 'error',
                        message: this.$vuetify.t(`$vuetify.messages.error.limits.${err.response.data.message}`),
                        locale: false
                    }, { root: true })
                }
            }).finally(() => {
                this.loading = false
                this.newLimitedTo = null
            })
        },
        async updateAmountField(accountId, index, field) {
            if (this.loading || !this.newAmount) return

            this.loading = true

            const res = await this.$store.dispatch('updateAccountAmount', {
                accountId, field,
                amount: parseInt(this.newAmount)
            })

            if (res == 204) {
                this.businesses[index][field] = parseInt(this.newAmount)
            }

            this.loading = false
            this.newAmount = null
        },
        disabledUpdateAmountPaid(amountToPay, amountPaid) {
            return amountPaid >= amountToPay
        },
        openAmountDialog(value) {
            this.newAmount = value
        }
    }
}
</script>
